import type { TeamSessionSummaryResponse } from '@frontend/core/lib/domains/reporting/types'
import type { StatCardData } from './formatter'
import { buildMetrics } from '../../components/dashboard-chart-utils'
import { useReportingState } from '../../lib/reporting-state'

interface DashboardPrintProps {
  summary?: TeamSessionSummaryResponse
  cards: StatCardData[]
}

/**
 * Print-only dashboard layout matching the Angular reporting tool's PDF output.
 * Hidden on screen (`hidden print:block`), shown only when printing.
 *
 * DESIGN DECISIONS:
 *  - Separate component (not CSS-only transforms) because the print layout is
 *    fundamentally different: no bars, no interactivity, different grid structure.
 *  - Inline styles (not Tailwind classes) because the global @media print rule
 *    `* { color: black !important }` overrides Tailwind's CSS-variable colors.
 *  - Font sizes (3mm/4mm) match the Angular tool's _print.scss variables.
 *  - Grid layout: 3 columns outer, 3 columns inner (label | value | seasonAvg)
 *    — matches the Angular tool's print grid exactly.
 *
 * See docs/print-export.md for the full print/PDF guide.
 */
export function DashboardPrint({ summary, cards }: DashboardPrintProps) {
  const { config } = useReportingState()
  const metrics = summary
    ? buildMetrics(
        summary.summaryMetrics,
        config.dashboard.chartMetrics,
        config
      )
    : []

  return (
    <div className='hidden print:block' style={{ color: '#000' }}>
      {/* Chart metrics — 3 across, each metric: label(10rem) | value | (avg) */}
      {metrics.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2
            style={{
              fontSize: '4mm',
              fontWeight: 'normal',
              marginBottom: '0.75rem'
            }}
          >
            Team performance against season average
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridAutoFlow: 'row',
              rowGap: '0.5rem',
              columnGap: '1rem'
            }}
          >
            {metrics.map((m) => (
              <div
                key={m.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '10rem 1fr 1fr',
                  alignItems: 'center',
                  fontSize: '3mm'
                }}
              >
                <span>{m.name}</span>
                <span>{m.displayValue}</span>
                <span>({m.seasonAvgValue})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stat cards — 3 across, no medals, plain text */}
      {cards.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            rowGap: '2rem',
            columnGap: '1rem'
          }}
        >
          {cards.map((card) => (
            <div key={card.label} style={{ paddingRight: '1rem' }}>
              <div style={{ fontSize: '3mm' }}>{card.label}</div>
              <div style={{ fontSize: '4mm', fontWeight: 'bold' }}>
                {card.value}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  marginTop: '0.25rem'
                }}
              >
                {card.topPerformers.map((p) => (
                  <div
                    key={p.displayName}
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      fontSize: '3mm'
                    }}
                  >
                    <span style={{ flex: 1 }}>{p.displayName}</span>
                    <span>{p.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
